import type { ReportStatistics, WeightTarget } from '../../models/end-client-reporting-package';
import { effectiveBase, resolveStatistics, weightValue } from './crosstab-stats';
import type { RespondentRecord, SurveyLayout, WeightOf } from './report-aggregation';

/**
 * Each respondent's weight for a deck, worked out from its records every time they load -- never
 * stored -- so the editor and a published link run the same arithmetic over the same respondents and
 * cannot disagree about a single figure.
 *
 * Weights are fixed on the whole sample, before the filter rail: filtering a weighted deck shows the
 * weighted figures of a subgroup, it does not weight the subgroup again to the totals.
 */

/** How far any target may be missed, as a share, before rim weighting counts as finished. */
const CONVERGENCE = 0.0001;
const MAX_ITERATIONS = 50;

export interface TargetDiagnostics {
  variableKey: string;
  label: string;
  rows: { answer: string; target: number; sample: number; achieved: number }[];
  /** Respondents with no single answer to the question, who are left out of its adjustment. */
  missing: number;
}

export interface WeightingDiagnostics {
  method: 'column' | 'targets';
  respondents: number;
  /** Respondents carrying a weight above zero. */
  weighted: number;
  /** Effective base over head count: how much precision the weighting costs. 1 is none. */
  efficiency: number;
  min: number;
  max: number;
  /** Rim weighting only: whether every target was met within tolerance, and how many passes it took. */
  converged?: boolean;
  iterations?: number;
  targets?: TargetDiagnostics[];
  /** Plain-English reasons the weighting is suspect, for the settings panel. */
  problems: string[];
}

export interface DeckWeighting {
  /** Absent when the deck is unweighted -- which every aggregation reads as "count heads". */
  weightOf?: WeightOf;
  diagnostics: WeightingDiagnostics | null;
}

const UNWEIGHTED: DeckWeighting = { diagnostics: null };

export function deckWeighting(
  records: RespondentRecord[],
  statistics: ReportStatistics | undefined,
  variableMap: Record<string, string>,
  layout: SurveyLayout,
): DeckWeighting {
  const weighting = resolveStatistics(statistics).weighting;
  if (!weighting || !records.length) return UNWEIGHTED;

  if (weighting.method === 'column') {
    const weights = new Map<RespondentRecord, number>();
    for (const record of records) weights.set(record, weightValue(record[weighting.column]));
    const diagnostics = describe('column', weights);
    const unweighted = records.length - diagnostics.weighted;
    if (diagnostics.weighted === 0) {
      // A source without the column -- an older wave, a different export -- would otherwise weigh
      // every respondent at nothing and blank the deck. Unweighted, and said so, is the honest read.
      diagnostics.problems.push(`Nobody in this data has a weight in ${weighting.column}, so the figures are unweighted.`);
      return { diagnostics };
    } else if (unweighted) {
      diagnostics.problems.push(
        `${unweighted} of ${records.length} respondents have no weight in ${weighting.column}, so count for nothing.`);
    }
    return { weightOf: record => weights.get(record) ?? 0, diagnostics };
  }

  return rimWeighting(records, weighting.targets, variableMap, layout,
    weighting.minWeight, weighting.maxWeight);
}

/** One weighting question, resolved to which respondents sit in which answer. */
interface Rim {
  target: WeightTarget;
  label: string;
  /** Answer label for each respondent with exactly one answer; absent for the rest. */
  answerOf: Map<RespondentRecord, string>;
  /** Target shares over the answers that have one, scaled to total 1. */
  shares: Map<string, number>;
}

function resolveRim(
  records: RespondentRecord[],
  target: WeightTarget,
  variableMap: Record<string, string>,
  layout: SurveyLayout,
): Rim | null {
  const position = variableMap[target.variableKey];
  const variable = position ? layout[position] : undefined;
  // Rim weighting needs answers that partition the sample, which only a single-select question has.
  if (!variable || variable.kind !== 'coded') return null;

  const labelOf = new Map(variable.options.map(option => [String(option.value), option.label]));
  const answerOf = new Map<RespondentRecord, string>();
  for (const record of records) {
    const value = record[variable.column];
    const codes = (Array.isArray(value) ? value : [value])
      .filter(code => code !== undefined && code !== null && String(code).trim() !== '')
      .map(code => String(code).trim());
    if (codes.length === 1) answerOf.set(record, labelOf.get(codes[0]) ?? codes[0]);
  }

  const positive = Object.entries(target.shares ?? {}).filter(([, share]) => share > 0);
  const sum = positive.reduce((total, [, share]) => total + share, 0);
  if (!sum) return null;
  return {
    target,
    label: variable.label,
    answerOf,
    shares: new Map(positive.map(([answer, share]) => [answer, share / sum])),
  };
}

/**
 * Rim weighting (raking): adjust everyone's weight to hit the first question's targets, then the
 * second's, and so on, and repeat until every target holds at once. Only respondents in an answer
 * with a target are adjusted on that question -- someone who skipped it, or gave an answer with no
 * target, keeps the weight the other questions gave them.
 *
 * Caps are applied on every pass rather than once at the end, so the targets are met by the capped
 * weights rather than missed by however much the cap moved them. Each pass scales the weights to
 * average 1 before capping, so a cap is a cap on the weights as finally used.
 */
function rimWeighting(
  records: RespondentRecord[],
  targets: WeightTarget[],
  variableMap: Record<string, string>,
  layout: SurveyLayout,
  minWeight?: number,
  maxWeight?: number,
): DeckWeighting {
  const problems: string[] = [];
  const rims = targets.flatMap(target => {
    const rim = resolveRim(records, target, variableMap, layout);
    if (!rim) problems.push(`${target.variableKey} cannot be weighted on: it is not a single-answer question with targets.`);
    return rim ? [rim] : [];
  });
  if (!rims.length) return { diagnostics: null };

  const weights = new Map<RespondentRecord, number>(records.map(record => [record, 1]));
  const sample = rims.map(rim => shares(rim, records, () => 1));
  const floor = minWeight && minWeight > 0 ? minWeight : 0;
  const ceiling = maxWeight && maxWeight > 0 ? maxWeight : Number.POSITIVE_INFINITY;

  let iterations = 0;
  let converged = false;
  while (iterations < MAX_ITERATIONS && !converged) {
    iterations++;
    for (const rim of rims) {
      const current = shares(rim, records, record => weights.get(record) ?? 0);
      const factor = new Map<string, number>();
      for (const [answer, target] of rim.shares) {
        const now = current.byAnswer.get(answer) ?? 0;
        if (now > 0) factor.set(answer, target / now);
      }
      for (const [record, answer] of rim.answerOf) {
        const adjust = factor.get(answer);
        if (adjust !== undefined) weights.set(record, (weights.get(record) ?? 0) * adjust);
      }
    }
    // Scaled to average 1 before capping, never after: a rescale after the caps would push the
    // capped weights straight back past them.
    normaliseToHeadCount(weights);
    for (const [record, weight] of weights) weights.set(record, Math.min(ceiling, Math.max(floor, weight)));
    converged = rims.every(rim => {
      const achieved = shares(rim, records, record => weights.get(record) ?? 0);
      return [...rim.shares].every(([answer, target]) =>
        Math.abs((achieved.byAnswer.get(answer) ?? 0) - target) < CONVERGENCE);
    });
  }

  // Without caps the weights can be scaled exactly to average 1; with them, the loop already did as
  // much as the caps allow.
  if (!floor && !Number.isFinite(ceiling)) normaliseToHeadCount(weights);

  const diagnostics = describe('targets', weights);
  diagnostics.converged = converged;
  diagnostics.iterations = iterations;
  diagnostics.targets = rims.map((rim, i) => {
    const achieved = shares(rim, records, record => weights.get(record) ?? 0);
    return {
      variableKey: rim.target.variableKey,
      label: rim.label,
      rows: [...rim.shares].map(([answer, target]) => ({
        answer,
        target: target * 100,
        sample: (sample[i].byAnswer.get(answer) ?? 0) * 100,
        achieved: (achieved.byAnswer.get(answer) ?? 0) * 100,
      })),
      missing: records.length - [...rim.answerOf.values()].filter(answer => rim.shares.has(answer)).length,
    };
  });
  for (const rim of diagnostics.targets) {
    for (const row of rim.rows) {
      if (row.sample === 0) problems.push(`Nobody answered "${row.answer}" to ${rim.label}, so its target cannot be met.`);
    }
  }
  if (!converged) {
    problems.push(`The targets could not all be met together after ${iterations} passes`
      + (Number.isFinite(ceiling) || floor ? ' -- the caps may be too tight.' : '.'));
  }
  diagnostics.problems.push(...problems);

  return { weightOf: record => weights.get(record) ?? 0, diagnostics };
}

/** Each targeted answer's share of the respondents who gave a targeted answer, by `weigh`. */
function shares(
  rim: Rim,
  records: RespondentRecord[],
  weigh: WeightOf,
): { byAnswer: Map<string, number>; inTargets: number } {
  const byAnswer = new Map<string, number>();
  let inTargets = 0;
  for (const record of records) {
    const answer = rim.answerOf.get(record);
    if (answer === undefined || !rim.shares.has(answer)) continue;
    const weight = weigh(record);
    byAnswer.set(answer, (byAnswer.get(answer) ?? 0) + weight);
    inTargets += weight;
  }
  if (inTargets > 0) {
    for (const [answer, weight] of byAnswer) byAnswer.set(answer, weight / inTargets);
  }
  return { byAnswer, inTargets };
}

/**
 * Scaled so the weights average 1: a weighted base then reads as roughly the head count, which is
 * what every `n=` in the deck was written to show.
 */
function normaliseToHeadCount(weights: Map<RespondentRecord, number>) {
  const total = [...weights.values()].reduce((sum, weight) => sum + weight, 0);
  if (!(total > 0)) return;
  const scale = weights.size / total;
  for (const [record, weight] of weights) weights.set(record, weight * scale);
}

function describe(method: 'column' | 'targets', weights: Map<RespondentRecord, number>): WeightingDiagnostics {
  let sum = 0;
  let squares = 0;
  let weighted = 0;
  let min = Number.POSITIVE_INFINITY;
  let max = 0;
  for (const weight of weights.values()) {
    if (!(weight > 0)) continue;
    weighted++;
    sum += weight;
    squares += weight * weight;
    min = Math.min(min, weight);
    max = Math.max(max, weight);
  }
  return {
    method,
    respondents: weights.size,
    weighted,
    efficiency: weights.size ? effectiveBase(sum, squares) / weights.size : 0,
    min: weighted ? min : 0,
    max,
    problems: [],
  };
}

/** One row per respondent, for a DP team to check or reuse the weights. */
export function weightsCsv(records: RespondentRecord[], weightOf: WeightOf, idColumn: string): string {
  const rows = records.map((record, i) => {
    const id = record[idColumn];
    const name = id === undefined || Array.isArray(id) ? String(i + 1) : String(id);
    return `${csvCell(name)},${weightOf(record).toFixed(6)}`;
  });
  return [`${csvCell(idColumn || 'respondent')},weight`, ...rows].join('\r\n');
}

export function csvCell(text: string): string {
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}
